import type { BadgeDefinition, EarnedBadge, VoterStats } from './types.js';

/**
 * Badges are tasteful and sports-oriented: they reward showing up and voting
 * consistently, never ranking "correctly". Nothing here changes ballot weight.
 */
export const BADGES: BadgeDefinition[] = [
  {
    id: 'first-ballot',
    name: 'First Ballot',
    description: 'Submitted your first FanVote25 Top 25.',
    icon: 'ballot',
    tier: 'bronze',
  },
  {
    id: 'streak-3',
    name: '3 Week Streak',
    description: 'Voted three weeks in a row.',
    icon: 'flame',
    tier: 'bronze',
  },
  {
    id: 'streak-6',
    name: 'Saturday Regular',
    description: 'Voted six weeks in a row.',
    icon: 'flame',
    tier: 'silver',
  },
  {
    id: 'ballots-10',
    name: '10 Ballots Cast',
    description: 'Ten career ballots submitted.',
    icon: 'stack',
    tier: 'bronze',
  },
  {
    id: 'ballots-25',
    name: '25 Ballots Cast',
    description: 'Twenty-five career ballots submitted.',
    icon: 'stack',
    tier: 'silver',
  },
  {
    id: 'ballots-50',
    name: '50 Ballots Cast',
    description: 'Fifty career ballots submitted.',
    icon: 'stack',
    tier: 'gold',
  },
  {
    id: 'perfect-attendance',
    name: 'Perfect Attendance',
    description: 'Voted every week of a season.',
    icon: 'trophy',
    tier: 'gold',
  },
  {
    id: 'early-voter',
    name: 'Early Voter',
    description: 'Submitted a ballot within three hours of the window opening.',
    icon: 'bolt',
    tier: 'bronze',
  },
  {
    id: 'poll-veteran',
    name: 'Poll Veteran',
    description: 'Participated in more than one FanVote25 season.',
    icon: 'shield',
    tier: 'platinum',
  },
  {
    id: 'make-your-case',
    name: 'Make Your Case',
    description: 'Attached a written case to a submitted ballot.',
    icon: 'quote',
    tier: 'bronze',
  },
  {
    id: 'consensus-builder',
    name: 'Consensus Builder',
    description: 'Finished a week within 90% of the final poll.',
    icon: 'target',
    tier: 'silver',
  },
  {
    id: 'contrarian',
    name: 'Contrarian',
    description: 'Ranked a team at least ten spots away from the consensus.',
    icon: 'spark',
    tier: 'silver',
  },
];

export const BADGES_BY_ID: Record<string, BadgeDefinition> = Object.fromEntries(
  BADGES.map((badge) => [badge.id, badge]),
);

export interface BadgeEvaluationContext {
  stats: VoterStats;
  weeksInSeason: number;
  /** Any ballot this season carried a written case. */
  hasWrittenCase: boolean;
  /** Any ballot submitted within three hours of its window opening. */
  votedEarly: boolean;
  /** Best single-week consensus score this season. */
  bestWeekScore: number | null;
  /** Largest gap between one of the voter's ranks and the poll's. */
  largestDeviation: number;
}

/** Pure badge evaluation; callers persist only the newly earned ids. */
export function evaluateBadges(context: BadgeEvaluationContext): string[] {
  const { stats } = context;
  const earned: string[] = [];

  if (stats.ballotsCast >= 1) earned.push('first-ballot');
  if (stats.longestStreak >= 3) earned.push('streak-3');
  if (stats.longestStreak >= 6) earned.push('streak-6');
  if (stats.ballotsCast >= 10) earned.push('ballots-10');
  if (stats.ballotsCast >= 25) earned.push('ballots-25');
  if (stats.ballotsCast >= 50) earned.push('ballots-50');
  if (context.weeksInSeason > 0 && stats.ballotsCast >= context.weeksInSeason) {
    earned.push('perfect-attendance');
  }
  if (context.votedEarly) earned.push('early-voter');
  if (stats.seasonsParticipated > 1) earned.push('poll-veteran');
  if (context.hasWrittenCase) earned.push('make-your-case');
  if ((context.bestWeekScore ?? 0) >= 90) earned.push('consensus-builder');
  if (context.largestDeviation >= 10) earned.push('contrarian');

  return earned;
}

/** Returns the badge ids in `earnedIds` that the user does not already hold. */
export function newlyEarned(earnedIds: string[], existing: EarnedBadge[]): string[] {
  const held = new Set(existing.map((badge) => badge.badgeId));
  return earnedIds.filter((id) => !held.has(id));
}
